import { computed, Injectable, signal } from '@angular/core';

/** Query param used to prefill the admin-settings view-submission URL field. */
export const ADMIN_SETTINGS_VIEW_SUBMISSION_URL_PARAM = 'viewSubmissionUrl';

export interface ViewSubmissionIds {
  briefId: string;
  creatorId: string;
}

/** Same-tab deep-link into brief tracker with the view-submission URL to paste. */
export function buildAdminSettingsViewSubmissionLink(viewSubmissionUrl: string): string {
  const params = new URLSearchParams({
    [ADMIN_SETTINGS_VIEW_SUBMISSION_URL_PARAM]: viewSubmissionUrl,
  });
  return `/admin-options/settings/brief-tracker?${params.toString()}`;
}

/**
 * Extracts brief + creator IDs from a view-submission URL or path.
 * Pattern: …/view-submission/{briefId}/{creatorId}
 */
export function parseViewSubmissionUrl(raw: string): ViewSubmissionIds | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  let pathname = trimmed;
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      pathname = new URL(trimmed).pathname;
    }
  } catch {
    return null;
  }

  const match = pathname.match(/\/view-submission\/([^/?#]+)\/([^/?#]+)\/?/i);
  if (!match?.[1] || !match[2]) {
    return null;
  }

  const briefId = decodeURIComponent(match[1]).trim();
  const creatorId = decodeURIComponent(match[2]).trim();
  if (!briefId || !creatorId) {
    return null;
  }

  return { briefId, creatorId };
}

@Injectable()
export class AdminSettingsContextService {
  readonly briefId = signal('');
  readonly creatorId = signal('');

  readonly hasBriefId = computed(() => this.briefId().trim().length > 0);
  readonly hasCreatorId = computed(() => this.creatorId().trim().length > 0);
  readonly hasAny = computed(() => this.hasBriefId() || this.hasCreatorId());
  readonly needsBriefId = computed(() => this.hasCreatorId() && !this.hasBriefId());
  readonly needsCreatorId = computed(() => this.hasBriefId() && !this.hasCreatorId());
  readonly hasBoth = computed(() => this.hasBriefId() && this.hasCreatorId());

  setBriefId(value: string): void {
    this.briefId.set(value.trim());
  }

  setCreatorId(value: string): void {
    this.creatorId.set(value.trim());
  }

  /** Applies IDs from a view-submission URL. Returns true when both IDs were extracted. */
  applyViewSubmissionUrl(raw: string): boolean {
    const parsed = parseViewSubmissionUrl(raw);
    if (!parsed) {
      return false;
    }
    this.setBriefId(parsed.briefId);
    this.setCreatorId(parsed.creatorId);
    return true;
  }

  clear(): void {
    this.briefId.set('');
    this.creatorId.set('');
  }
}
